import { useMemo, useState } from 'react';
import {
  CalendarClock, Check, ChevronRight, ClipboardList, Eye, EyeOff, FileText,
  History, Pencil, Plus, ShieldAlert, ShieldCheck,
} from 'lucide-react';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Input, Textarea } from '@/components/ui/Input';
import { FormField } from '@/components/ui/FormField';
import { SegmentedControl } from '@/components/ui/SegmentedControl';
import { Switch } from '@/components/ui/Switch';
import { SkeletonCard } from '@/components/ui/Skeleton';
import { EmptyState, ErrorState } from '@/components/ui/States';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/Dialog';
import { ApiError } from '@/lib/api/client';
import { cn } from '@/lib/cn';
import { toast } from '@/hooks/useToast';
import { useConsentTemplates, usePublishConsentTemplate, useUpdateConsentTemplate } from '@/features/consent/useConsent';
import type { ConsentTemplateRow, ConsentTypeId, ConsentTypeMeta } from '@/features/consent/types';

/**
 * Admin Panel → Clinical Setup → Consent Forms (admin:consent).
 *
 * ── Why master–detail rather than a stack of cards ──────────────
 *
 * The first version of this screen stacked one tall card per consent form,
 * each with its settings, its full consent text and its version history
 * expanded inline. With three forms that is already several screens of
 * scrolling, and the wording — the longest thing on the page by an order of
 * magnitude — pushed the settings an Admin actually came to change off the
 * top. A hospital that adds two more forms makes it worse linearly.
 *
 * So: a list on the left answering "what do we ask, and what is compulsory",
 * and one form's detail on the right. That is the assessment's §2/§3 finding
 * (information at a glance; hierarchy before fields) applied to this screen —
 * see docs/HMS_V4_UI_MODERNIZATION_ASSESSMENT.md.
 *
 * ── The one rule the UI has to teach ────────────────────────────
 *
 * Settings change in place. Wording never does — editing it publishes a NEW
 * version and retires the current one, so a patient who already consented
 * keeps the exact text and version they agreed to. The backend enforces it
 * (`PATCH` rejects `title`/`body`); this screen states it wherever an Admin
 * is about to act, rather than letting them discover it as an error.
 */

function validityLabel(days: number | null): string {
  if (!days) return 'No expiry';
  if (days % 365 === 0) return `${days / 365} year${days === 365 ? '' : 's'}`;
  if (days % 30 === 0) return `${days / 30} month${days === 30 ? '' : 's'}`;
  return `${days} days`;
}

function formatDate(value: string | null): string {
  if (!value) return '—';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString();
}

function errMsg(e: unknown, fallback: string): string {
  return e instanceof ApiError ? e.message : fallback;
}

/** Mirrors backend/utils/patientConsent.js's slugifyConsentType, for the live preview. */
function slugify(value: string): string {
  return value.trim().toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '').slice(0, 60);
}

interface ConsentForm {
  type: ConsentTypeId;
  name: string;
  versions: ConsentTemplateRow[];
  live: ConsentTemplateRow | null;
  settings: ConsentTypeMeta | null;
}

export default function ConsentFormsTab() {
  const { data, isLoading, error, refetch } = useConsentTemplates();
  const [creating, setCreating] = useState(false);
  const [selectedType, setSelectedType] = useState<ConsentTypeId | null>(null);

  // Group every version under its consent form. Built from `templates`, not
  // from `types`, on purpose: a form whose versions have ALL been retired
  // drops out of `types` (it has no active row to read settings from) but must
  // still be listed here, or an Admin could never reactivate it.
  const forms: ConsentForm[] = useMemo(() => {
    const rows = data?.templates || [];
    const settingsByType = new Map((data?.types || []).map((t) => [t.type, t]));
    const byType = new Map<ConsentTypeId, ConsentTemplateRow[]>();
    for (const row of rows) {
      const list = byType.get(row.consent_type) || [];
      list.push(row);
      byType.set(row.consent_type, list);
    }
    return [...byType.entries()].map(([type, versions]) => {
      const live = versions.find((v) => v.is_active) || null;
      return {
        type,
        versions, // already newest-first from the API
        live,
        settings: settingsByType.get(type) || null,
        name: live?.label || versions[0]?.label || type,
      };
    });
  }, [data]);

  const selected = forms.find((f) => f.type === selectedType) || forms[0] || null;

  if (isLoading) {
    return (
      <div className="grid gap-4 lg:grid-cols-[320px_minmax(0,1fr)]">
        <SkeletonCard />
        <SkeletonCard />
      </div>
    );
  }
  if (error) {
    return <ErrorState message={errMsg(error, 'Failed to load consent forms')} onRetry={() => refetch()} />;
  }

  const askedCount = forms.filter((f) => f.live?.is_enabled).length;
  const mandatoryCount = forms.filter((f) => f.live?.is_enabled && f.live.is_mandatory).length;

  return (
    <div className="flex flex-col gap-4">
      <PageIntro
        total={forms.length}
        asked={askedCount}
        mandatory={mandatoryCount}
        onCreate={() => setCreating(true)}
      />

      {forms.length === 0 ? (
        <EmptyState
          label="No consent forms yet"
          hint="Create one to start asking patients for consent during Registration."
          action={{ label: '+ Create consent form', onClick: () => setCreating(true) }}
        />
      ) : (
        <div className="grid items-start gap-4 lg:grid-cols-[320px_minmax(0,1fr)]">
          <ConsentFormList
            forms={forms}
            selectedType={selected?.type ?? null}
            onSelect={setSelectedType}
          />
          {selected && <ConsentFormDetail key={selected.type} form={selected} />}
        </div>
      )}

      <CreateConsentFormDialog
        open={creating}
        existingTypes={forms.map((f) => f.type)}
        onClose={() => setCreating(false)}
        onCreated={(type) => { setCreating(false); setSelectedType(type); }}
      />
    </div>
  );
}

// ── Header ───────────────────────────────────────────────────────

function PageIntro({
  total, asked, mandatory, onCreate,
}: { total: number; asked: number; mandatory: number; onCreate: () => void }) {
  return (
    <Card className="flex flex-wrap items-center gap-x-6 gap-y-4">
      <div className="min-w-[260px] flex-1">
        <div className="text-[13px] text-ink-muted">
          The consent forms patients are asked to accept at Registration. <strong className="text-ink">Status</strong>{' '}
          decides whether a form is asked for at all; <strong className="text-ink">Requirement</strong> decides whether
          Registration can be completed without it.
        </div>
      </div>

      {/* The three numbers an Admin opens this screen to check. Kept as plain
          counts rather than KpiTiles: these are a legend for the list beside
          them, not metrics worth the visual weight of a dashboard tile. */}
      <div className="flex items-center gap-5">
        <Stat icon={<ClipboardList className="h-3.5 w-3.5" />} value={total} label={total === 1 ? 'form' : 'forms'} />
        <Stat icon={<Eye className="h-3.5 w-3.5" />} value={asked} label="asked at registration" />
        <Stat icon={<ShieldAlert className="h-3.5 w-3.5" />} value={mandatory} label="mandatory" tone={mandatory > 0 ? 'warn' : 'muted'} />
      </div>

      <Button onClick={onCreate}>
        <Plus className="h-4 w-4" /> New consent form
      </Button>
    </Card>
  );
}

function Stat({
  icon, value, label, tone = 'muted',
}: { icon: React.ReactNode; value: number; label: string; tone?: 'muted' | 'warn' }) {
  return (
    <div className="flex items-center gap-2">
      <span className={cn('shrink-0', tone === 'warn' ? 'text-warn-ink' : 'text-muted-foreground')}>{icon}</span>
      <div className="leading-tight">
        <div className="text-[17px] font-semibold tabular-nums">{value}</div>
        <div className="text-[10.5px] uppercase tracking-wide text-muted-foreground">{label}</div>
      </div>
    </div>
  );
}

// ── Left: the list of forms ──────────────────────────────────────

function ConsentFormList({
  forms, selectedType, onSelect,
}: { forms: ConsentForm[]; selectedType: ConsentTypeId | null; onSelect: (type: ConsentTypeId) => void }) {
  return (
    <nav aria-label="Consent forms" className="overflow-hidden rounded-xl border border-border bg-card lg:sticky lg:top-5">
      <div className="border-b border-border px-3.5 py-2 text-[11px] font-bold uppercase tracking-wide text-ink-muted">
        Consent forms
      </div>
      <ul className="flex flex-col">
        {forms.map((form) => {
          const active = form.type === selectedType;
          const enabled = !!form.live?.is_enabled;
          const mandatory = !!form.live?.is_mandatory;
          return (
            <li key={form.type}>
              <button
                type="button"
                onClick={() => onSelect(form.type)}
                aria-current={active ? 'true' : undefined}
                className={cn(
                  'flex w-full items-center gap-2.5 border-b border-border px-3.5 py-3 text-left transition-colors last:border-b-0',
                  active ? 'bg-accent' : 'hover:bg-muted'
                )}
              >
                {/* Status reads as an icon before the name, so the list answers
                    "what do we actually ask?" without the eye travelling to a
                    badge column on the right. */}
                <span className={cn('shrink-0', enabled ? 'text-success-ink' : 'text-muted-foreground')}>
                  {enabled ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
                </span>
                <span className="min-w-0 flex-1">
                  <span className={cn('block truncate text-[13px] font-semibold', active && 'text-teal')}>
                    {form.name}
                  </span>
                  <span className="mt-0.5 flex flex-wrap items-center gap-1.5 text-[11px] text-muted-foreground">
                    {form.live ? <>v{form.live.version}</> : <span className="text-warn-ink">No live version</span>}
                    <span aria-hidden>·</span>
                    {validityLabel(form.live?.validity_days ?? null)}
                  </span>
                </span>
                {mandatory && <Badge variant="danger">Required</Badge>}
                {!enabled && form.live && <Badge variant="neutral">Off</Badge>}
                <ChevronRight className={cn('h-3.5 w-3.5 shrink-0 text-muted-foreground', active && 'text-teal')} />
              </button>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}

// ── Right: one form's detail ─────────────────────────────────────

function ConsentFormDetail({ form }: { form: ConsentForm }) {
  const { type, name, live, versions, settings } = form;
  const publish = usePublishConsentTemplate();
  const update = useUpdateConsentTemplate();

  const [editing, setEditing] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [error, setError] = useState('');

  const [draftTitle, setDraftTitle] = useState(live?.title || '');
  const [draftBody, setDraftBody] = useState(live?.body || '');
  const [draftValidity, setDraftValidity] = useState(live?.validity_days != null ? String(live.validity_days) : '');

  // Re-seed the expiry box when the live version's own value changes — after
  // this admin publishes, or after the query refetches someone else's edit.
  // Adjusting state during render (React's documented pattern) rather than in
  // an effect: an effect would render the stale number first, and without any
  // re-seeding the box could silently write back a value nobody is looking at.
  const [seededValidity, setSeededValidity] = useState(live?.validity_days ?? null);
  if ((live?.validity_days ?? null) !== seededValidity) {
    setSeededValidity(live?.validity_days ?? null);
    setDraftValidity(live?.validity_days != null ? String(live.validity_days) : '');
  }

  const enabled = !!live?.is_enabled;
  const mandatory = !!live?.is_mandatory;
  // The one form whose Status is also a send rule: the WhatsApp sender reads
  // it to decide who may be messaged (backend whatsappGateway.consentPolicy).
  const drivesWhatsApp = type === 'comm_whatsapp';
  const nextVersion = (versions[0]?.version ?? 0) + 1;
  const busy = update.isPending || publish.isPending;

  const startEditing = () => {
    setDraftTitle(live?.title || '');
    setDraftBody(live?.body || '');
    setError('');
    setEditing(true);
  };

  const patch = async (input: Parameters<typeof update.mutateAsync>[0], message: string) => {
    setError('');
    try {
      await update.mutateAsync(input);
      toast({ title: name, description: message });
    } catch (e) { setError(errMsg(e, 'Could not save that change')); }
  };

  const toggleEnabled = (next: boolean) => {
    if (!live) return;
    // A disabled form can never be mandatory — the backend and a database CHECK
    // both refuse it. Rather than showing an error for a combination the UI let
    // them pick, disabling also drops Requirement to Optional, and says so.
    patch(
      { id: live.id, is_enabled: next, ...(!next && mandatory ? { is_mandatory: false } : {}) },
      (next
        ? 'Enabled — this consent will be asked for during Registration.'
        : `Disabled${mandatory ? ' and set to Optional' : ''} — it will no longer be asked for during Registration.`)
      + (!drivesWhatsApp ? '' : next
        ? ' WhatsApp messages now go only to patients who have accepted it.'
        : ' WhatsApp messages now go to every patient except those who declined.')
    );
  };

  const setRequirement = (value: 'mandatory' | 'optional') => {
    if (!live) return;
    patch(
      { id: live.id, is_mandatory: value === 'mandatory' },
      value === 'mandatory'
        ? 'Mandatory — Registration cannot be completed unless the patient accepts.'
        : 'Optional — a patient may decline and still be registered.'
    );
  };

  const saveValidity = () =>
    live && patch(
      { id: live.id, validity_days: draftValidity.trim() === '' ? null : Number(draftValidity) },
      'Expiry period updated. Consents already recorded keep the expiry date they were given.'
    );

  const doPublish = async () => {
    setError('');
    try {
      await publish.mutateAsync({
        consent_type: type,
        title: draftTitle.trim(),
        body: draftBody,
        validity_days: draftValidity.trim() === '' ? null : Number(draftValidity),
      });
      setEditing(false);
      toast({
        title: `${name} — version ${nextVersion} published`,
        description: 'Patients who already consented keep the version they agreed to.',
      });
    } catch (e) { setError(errMsg(e, 'Failed to publish new version')); }
  };

  const activateVersion = (row: ConsentTemplateRow) =>
    patch({ id: row.id, is_active: true }, `Version ${row.version} is now the live wording.`);

  const validityChanged = (live?.validity_days ?? null) !== (draftValidity.trim() === '' ? null : Number(draftValidity));

  return (
    <div className="flex min-w-0 flex-col gap-4">
      {/* Identity */}
      <Card>
        <div className="flex flex-wrap items-start gap-x-3 gap-y-2">
          <div className="min-w-0 flex-1">
            <h3 className="truncate text-[17px] font-semibold">{name}</h3>
            <div className="mt-1 flex flex-wrap items-center gap-2 text-[11.5px] text-muted-foreground">
              <span className="font-mono">{type}</span>
              {live && <><span aria-hidden>·</span><span>Live version {live.version}</span></>}
              {live && <><span aria-hidden>·</span><span>{versions.length} version{versions.length === 1 ? '' : 's'}</span></>}
            </div>
          </div>
          <div className="flex shrink-0 flex-wrap items-center gap-1.5">
            {live ? (
              enabled
                ? <Badge variant="success"><Check className="mr-1 h-3 w-3" />Asked at registration</Badge>
                : <Badge variant="neutral">Not asked</Badge>
            ) : <Badge variant="warn">No live version</Badge>}
            {enabled && (mandatory
              ? <Badge variant="danger">Mandatory</Badge>
              : <Badge variant="info">Optional</Badge>)}
          </div>
        </div>

        {settings?.description && <p className="mt-3 text-[12.5px] text-ink-muted">{settings.description}</p>}

        {error && (
          <div className="mt-3 rounded-lg border border-danger bg-danger-soft px-3 py-2 text-[12.5px] text-danger-ink">{error}</div>
        )}

        {!live && (
          <div className="mt-3 rounded-lg border border-warn bg-warn-soft px-3 py-2 text-[12.5px] text-warn-ink">
            Every version of this form has been retired, so it is not asked for anywhere and its settings cannot be
            changed. Make a version live again from the history below, or publish new wording.
          </div>
        )}
      </Card>

      {/* Settings. All three change in place — none of them creates a version,
          because none of them is the agreement the patient signs. */}
      <Card>
        <div className="mb-4 flex items-center gap-2 text-[11.5px] font-bold uppercase tracking-wide text-ink-muted">
          <ShieldCheck className="h-3.5 w-3.5" /> Settings
        </div>

        <div className="flex flex-col divide-y divide-border">
          <SettingRow
            title="Status"
            help={(enabled
              ? 'Shown on the Consent step during Registration.'
              : 'Hidden from Registration. Consents already given stay on record and can still be recorded or withdrawn from Patient Records.')
              + (!drivesWhatsApp ? '' : enabled
                ? ' WhatsApp messages go only to patients who have accepted this consent. If you disable it, they go to every patient except those who declined.'
                : ' While disabled, WhatsApp messages go to every patient except those who declined this consent — including patients who were never asked.')}
            control={
              <label className="flex cursor-pointer items-center gap-2.5">
                <Switch checked={enabled} onCheckedChange={toggleEnabled} disabled={!live || busy} />
                <span className={cn('text-[12.5px] font-semibold', enabled ? 'text-success-ink' : 'text-muted-foreground')}>
                  {enabled ? 'Enabled' : 'Disabled'}
                </span>
              </label>
            }
          />

          <SettingRow
            title="Requirement"
            help={!enabled
              ? 'Enable this form before it can be made mandatory — a form that is never asked could never be accepted.'
              : mandatory
                ? 'Registration cannot be completed unless the patient accepts.'
                : 'The patient may accept or decline; either way Registration continues.'}
            control={
              <SegmentedControl<'mandatory' | 'optional'>
                value={live ? (mandatory ? 'mandatory' : 'optional') : null}
                onChange={setRequirement}
                disabled={!live || !enabled || busy}
                options={[
                  { value: 'mandatory', label: 'Mandatory', tone: 'danger' },
                  { value: 'optional', label: 'Optional', tone: 'success' },
                ]}
              />
            }
          />

          <SettingRow
            title="Expiry period"
            help={`Currently ${validityLabel(live?.validity_days ?? null).toLowerCase()}. After it lapses, staff are asked for this consent again. Consents already recorded keep the expiry date they were given.`}
            control={
              <div className="flex items-center gap-2">
                <div className="relative">
                  <CalendarClock className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    type="number"
                    min={1}
                    aria-label="Expiry period in days"
                    value={draftValidity}
                    onChange={(e) => setDraftValidity(e.target.value)}
                    placeholder="Never expires"
                    disabled={!live || busy}
                    className="w-[168px] pl-8"
                  />
                </div>
                <Button variant="ghost" size="sm" onClick={saveValidity} disabled={!live || busy || !validityChanged}>
                  Save
                </Button>
              </div>
            }
          />
        </div>
      </Card>

      {/* Wording */}
      <Card>
        <div className="mb-4 flex flex-wrap items-center gap-2">
          <div className="flex items-center gap-2 text-[11.5px] font-bold uppercase tracking-wide text-ink-muted">
            <FileText className="h-3.5 w-3.5" /> Consent wording
          </div>
          {!editing && (
            <div className="ml-auto flex gap-2">
              <Button variant="ghost" size="sm" onClick={() => setShowHistory((v) => !v)}>
                <History className="h-3.5 w-3.5" /> {versions.length} version{versions.length === 1 ? '' : 's'}
              </Button>
              <Button size="sm" onClick={startEditing}>
                <Pencil className="h-3.5 w-3.5" /> Edit wording
              </Button>
            </div>
          )}
        </div>

        {editing ? (
          <div className="flex flex-col gap-3.5">
            <FormField label="Title — the heading shown above the wording" required>
              <Input value={draftTitle} onChange={(e) => setDraftTitle(e.target.value)} placeholder="e.g. Consent to Treatment" />
            </FormField>
            <FormField label="Consent text — what is read to, and accepted by, the patient" required>
              <Textarea
                rows={16}
                value={draftBody}
                onChange={(e) => setDraftBody(e.target.value)}
                placeholder="The wording read to, and accepted by, the patient."
                className="font-normal leading-relaxed"
              />
            </FormField>
            <div className="rounded-lg border border-info bg-info-soft px-3 py-2.5 text-[12.5px] text-info-ink">
              <strong>This publishes version {nextVersion}</strong> and retires version {live?.version ?? '—'}. Consent
              text is never edited in place, so every consent already recorded keeps the exact text and version that
              patient agreed to. Status, Requirement and the expiry period carry over unchanged.
            </div>
            <div className="flex gap-2">
              <Button onClick={doPublish} disabled={publish.isPending || !draftTitle.trim() || !draftBody.trim()} loading={publish.isPending}>
                {publish.isPending ? 'Publishing…' : `Publish version ${nextVersion}`}
              </Button>
              <Button variant="ghost" onClick={() => setEditing(false)} disabled={publish.isPending}>Cancel</Button>
            </div>
          </div>
        ) : (
          <>
            {live?.title && <div className="mb-2 text-[13.5px] font-semibold">{live.title}</div>}
            <div className="max-h-[320px] overflow-y-auto whitespace-pre-wrap rounded-lg border border-border bg-background p-3.5 text-[12.5px] leading-relaxed text-ink">
              {live?.body || 'No consent text published yet.'}
            </div>
          </>
        )}

        {showHistory && !editing && (
          <div className="mt-4 border-t border-border pt-4">
            <div className="mb-2.5 text-[12px] text-ink-muted">
              Retired versions are kept forever — they are what a patient's older consent record resolves to.
            </div>
            <ol className="flex flex-col">
              {versions.map((v, i) => (
                <li key={v.id} className="flex gap-3">
                  {/* A rail rather than a bordered box per row: the point of
                      this list is the sequence, and a timeline says "these are
                      successive versions of one thing" without a legend. */}
                  <div className="flex flex-col items-center">
                    <span className={cn(
                      'mt-1.5 h-2 w-2 shrink-0 rounded-full',
                      v.is_active ? 'bg-success ring-4 ring-success-soft' : 'bg-line'
                    )} />
                    {i < versions.length - 1 && <span className="w-px flex-1 bg-border" />}
                  </div>
                  <div className="flex min-w-0 flex-1 flex-wrap items-center gap-x-2 gap-y-1 pb-4 text-[12px]">
                    <span className="font-semibold">Version {v.version}</span>
                    {v.is_active ? <Badge variant="success">Live</Badge> : <Badge variant="neutral">Retired</Badge>}
                    <span className="truncate text-ink-muted">{v.title}</span>
                    <span className="text-muted-foreground">· {validityLabel(v.validity_days)}</span>
                    <span className="text-muted-foreground">
                      · {v.consent_count} consent{v.consent_count === 1 ? '' : 's'} recorded
                    </span>
                    <span className="ml-auto whitespace-nowrap text-muted-foreground">
                      {formatDate(v.created_at)}{v.created_by ? ` · ${v.created_by}` : ''}
                    </span>
                    {!v.is_active && (
                      <Button size="sm" variant="ghost" onClick={() => activateVersion(v)} disabled={busy}>
                        Make live
                      </Button>
                    )}
                  </div>
                </li>
              ))}
            </ol>
          </div>
        )}
      </Card>
    </div>
  );
}

/** One label + explanation + control row. The explanation is not optional —
 *  every setting here changes what patients are asked and what staff can do. */
function SettingRow({ title, help, control }: { title: string; help: string; control: React.ReactNode }) {
  return (
    <div className="flex flex-wrap items-start justify-between gap-x-6 gap-y-2.5 py-3.5 first:pt-0 last:pb-0">
      <div className="min-w-[220px] max-w-[460px] flex-1">
        <div className="text-[13px] font-semibold">{title}</div>
        <div className="mt-0.5 text-[12px] leading-relaxed text-ink-muted">{help}</div>
      </div>
      <div className="shrink-0">{control}</div>
    </div>
  );
}

// ── Create a brand-new consent form ──────────────────────────────

function CreateConsentFormDialog({
  open, existingTypes, onClose, onCreated,
}: {
  open: boolean;
  existingTypes: ConsentTypeId[];
  onClose: () => void;
  onCreated: (type: ConsentTypeId) => void;
}) {
  const publish = usePublishConsentTemplate();
  const [name, setName] = useState('');
  // Left blank by default and derived from the name — an Admin should not have
  // to invent an identifier, but can override it if they want a specific one
  // (e.g. to match a code their reporting already uses).
  const [typeInput, setTypeInput] = useState('');
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [validity, setValidity] = useState('');
  const [enabled, setEnabled] = useState(true);
  const [requirement, setRequirement] = useState<'mandatory' | 'optional'>('optional');
  const [error, setError] = useState('');

  const resolvedType = slugify(typeInput || name);
  const duplicate = !!resolvedType && existingTypes.includes(resolvedType);
  const canSubmit = !!name.trim() && !!title.trim() && !!body.trim() && !!resolvedType && !duplicate;

  const reset = () => {
    setName(''); setTypeInput(''); setTitle(''); setBody(''); setValidity('');
    setEnabled(true); setRequirement('optional'); setError('');
  };

  const close = () => { reset(); onClose(); };

  const submit = async () => {
    setError('');
    try {
      const created = await publish.mutateAsync({
        consent_type: resolvedType,
        label: name.trim(),
        title: title.trim(),
        body,
        validity_days: validity.trim() === '' ? null : Number(validity),
        is_enabled: enabled,
        is_mandatory: requirement === 'mandatory' && enabled,
      });
      toast({ title: `${name.trim()} created`, description: 'Published as version 1.' });
      reset();
      onCreated(created.consent_type);
    } catch (e) { setError(errMsg(e, 'Failed to create the consent form')); }
  };

  return (
    <Dialog open={open} onOpenChange={(next) => { if (!next && !publish.isPending) close(); }}>
      <DialogContent className="max-h-[88vh] overflow-y-auto sm:max-w-[680px]">
        <DialogHeader>
          <DialogTitle>Create consent form</DialogTitle>
          <DialogDescription>
            This publishes version 1 of a new consent form. It behaves exactly like the built-in ones — it is versioned,
            it can expire, and editing its wording later creates version 2 rather than changing what patients have
            already agreed to.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-4">
          {error && <div className="rounded-lg border border-danger bg-danger-soft px-3 py-2 text-[12.5px] text-danger-ink">{error}</div>}

          <div className="grid gap-4 sm:grid-cols-2">
            <FormField label="Consent name" required>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Organ Donation Consent"
                autoFocus
              />
              <FieldHint>What this form is called on the Registration screen.</FieldHint>
            </FormField>

            <FormField label="Consent type" error={duplicate ? 'A consent form with this type already exists' : undefined}>
              <Input
                value={typeInput}
                onChange={(e) => setTypeInput(e.target.value)}
                placeholder={slugify(name) || 'organ_donation'}
                invalid={duplicate}
              />
              <FieldHint>
                Permanent identifier stored on every consent record; cannot be changed later. Leave blank to use{' '}
                <span className="font-mono text-ink-muted">{slugify(name) || '—'}</span>.
              </FieldHint>
            </FormField>
          </div>

          <FormField label="Title — the heading shown above the wording" required>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Consent to Organ Donation" />
          </FormField>

          <FormField label="Consent text — what is read to, and accepted by, the patient" required>
            <Textarea
              rows={10}
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder="The wording read to, and accepted by, the patient."
              className="leading-relaxed"
            />
          </FormField>

          <div className="grid gap-4 sm:grid-cols-3">
            <div>
              <div className="lbl">Status</div>
              <label className="flex cursor-pointer items-center gap-2.5">
                <Switch
                  checked={enabled}
                  onCheckedChange={(next) => { setEnabled(next); if (!next) setRequirement('optional'); }}
                />
                <span className={cn('text-[12.5px] font-semibold', enabled ? 'text-success-ink' : 'text-muted-foreground')}>
                  {enabled ? 'Enabled' : 'Disabled'}
                </span>
              </label>
            </div>
            <div>
              <div className="lbl">Requirement</div>
              <SegmentedControl<'mandatory' | 'optional'>
                value={requirement}
                onChange={setRequirement}
                disabled={!enabled}
                options={[
                  { value: 'mandatory', label: 'Mandatory', tone: 'danger' },
                  { value: 'optional', label: 'Optional', tone: 'success' },
                ]}
              />
            </div>
            <FormField label="Expiry (days)">
              <Input
                type="number"
                min={1}
                value={validity}
                onChange={(e) => setValidity(e.target.value)}
                placeholder="Never expires"
              />
            </FormField>
          </div>

          {requirement === 'mandatory' && enabled && (
            <div className="rounded-lg border border-warn bg-warn-soft px-3 py-2.5 text-[12.5px] text-warn-ink">
              <strong>Mandatory blocks Registration.</strong> No new patient can complete Registration without accepting
              this. Patients already registered are not affected at the Register stage — they are asked by the
              consent-due banner on their next visit.
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={close} disabled={publish.isPending}>Cancel</Button>
          <Button onClick={submit} disabled={!canSubmit || publish.isPending} loading={publish.isPending}>
            {publish.isPending ? 'Creating…' : 'Create consent form'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function FieldHint({ children }: { children: React.ReactNode }) {
  return <div className="mt-1 text-[11.5px] leading-relaxed text-muted-foreground">{children}</div>;
}
